import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { ALL_COLUMNS } from '../config/columnConfig';
import { createUserList } from '../services/api';
import { publicFetch } from '../utils/authenticatedFetch';
import CONFIG from '../config/config';
import './SettingsDialog.css'; // Reuse the same styling

// Generate field mappings dynamically from ALL_COLUMNS
const generateFieldMappings = () => {
  const mappings = {};
  const filterableColumns = [];
  const usedFieldsMap = new Map(); // Track fields we've already added by their database field
  
  // Field categories for better organization
  const categories = [
    { id: 'price', name: 'Price & Value Metrics', pattern: /price|value|pe|peg|book|yield|ratio/ },
    { id: 'growth', name: 'Growth & Change Metrics', pattern: /growth|change|percent/ },
    { id: 'fundamental', name: 'Fundamental Data', pattern: /cap|margin|employee|outlook|short|beta|signal/ },
    { id: 'meta', name: 'General Information', pattern: /date|company|industry|note/ },
  ];
  
  // Detect columns that should filter on change percentage rather than the value itself.
  // This map is derived ENTIRELY from ALL_COLUMNS — it used to be seeded with
  // hardcoded 'stockPrice'/'extendedHours' entries that the loop below already
  // reproduced exactly (issue #272). The seed was not merely redundant: this map
  // is consulted BEFORE both `column.filterField` and `column.sortField`, so any
  // hardcoded entry silently outranks the column's own declaration in
  // columnConfig.js and lets the two drift apart with nothing failing.
  const detectDisplayChangeColumns = () => {
    const map = {};
    
    // Look for columns with pattern: [value, changePercent] in fields
    ALL_COLUMNS.forEach(column => {
      if (column.fields && column.fields.length > 1) {
        // Check if any fields look like change percentage fields
        const changeFields = column.fields.filter(field => 
          field.toLowerCase().includes('change') && 
          field.toLowerCase().includes('percent')
        );
        
        // If we found change percentage fields and this isn't already mapped
        if (changeFields.length > 0 && !map[column.key]) {
          // No log here: every entry in this map is auto-detected by construction
          // now, and the "Applied special change field mapping" log below already
          // reports each one — a second line per column would be pure duplication.
          map[column.key] = changeFields[0]; // Use the first change percentage field
        }
      }
    });
    
    return map;
  };
  
  // Special mapping for columns that should filter on change percentage rather than the value itself
  const changeFieldMap = detectDisplayChangeColumns();
  
  // Helper function to detect change-related fields
  const isChangeField = (field) => {
    return field.toLowerCase().includes('change') || 
           field.toLowerCase().includes('percent') ||
           field.toLowerCase().includes('growth');
  };
  
  // Helper function to get a descriptive label for change fields
  const getChangeLabel = (field, baseLabel) => {
    if (field.toLowerCase().includes('percent')) {
      return `${baseLabel} %`;
    } else if (field.toLowerCase().includes('growth')) {
      return `${baseLabel} Growth`;
    } else if (field.toLowerCase().includes('change')) {
      return `${baseLabel} Change`;
    } else {
      return `${baseLabel} (Change)`;
    }
  };
  
  // Helper function to determine column category
  const getColumnCategory = (column) => {
    const text = `${column.key} ${column.label}`.toLowerCase();
    
    // Check against each category pattern
    for (const category of categories) {
      if (category.pattern.test(text)) {
        return category.id;
      }
    }
    
    // Apply some heuristics for common fields that might not match patterns
    if (text.includes('traffic') || text.includes('position') || text.includes('opening')) {
      return 'growth';
    }
    
    if (text.includes('earning') || text.includes('since')) {
      return 'meta';
    }
    
    // Default fallback
    return 'other';
  };
  
  // Helper function to determine if a field is filterable (can be used in database queries)
  const isFilterableField = (field) => {
    // Skip fields that are likely to be calculated or client-side only
    const nonFilterablePatterns = [
      /html/i, /component/i, /element/i, /node/i, /jsx/i, /react/i, 
      /display/i, /format/i, /icon/i, /image/i, /color/i
    ];
    
    for (const pattern of nonFilterablePatterns) {
      if (pattern.test(field)) {
        return false;
      }
    }
    
    return true;
  };
  
  // Helper function to add a column while checking for duplicates
  const addColumn = (column, dbField) => {
    // Skip if we already have a column using this database field
    if (dbField && usedFieldsMap.has(dbField)) {
      return false;
    }
    
    // Check for columns with very similar labels (to prevent duplication)
    const normalizedLabel = column.label.toLowerCase().replace(/[%()]/g, '').trim();
    const duplicate = filterableColumns.find(col => {
      const existingLabel = col.label.toLowerCase().replace(/[%()]/g, '').trim();
      return existingLabel === normalizedLabel;
    });
    
    if (duplicate) {
      console.log(`Skipping duplicate column: ${column.label} (similar to ${duplicate.label})`);
      return false;
    }
    
    // Add the column and track the database field
    filterableColumns.push({
      ...column,
      category: getColumnCategory(column) // Add category for grouping
    });
    
    if (dbField) {
      usedFieldsMap.set(dbField, column.key);
    }
    return true;
  };
  
  // Helper function to detect if a column is primarily about change/growth
  const isPrimaryChangeColumn = (column) => {
    // Check column key and label
    const key = column.key.toLowerCase();
    const label = column.label.toLowerCase();
    
    return key.includes('change') || 
           key.includes('growth') || 
           key.includes('percent') ||
           label.includes('change') || 
           label.includes('growth') || 
           label.includes('percent');
  };
  
  // Process all columns
  ALL_COLUMNS.forEach(column => {
    // A column merged onto rows client-side (Guidance, issue #306) has no screen_query field
    // behind it, so a filter on it could never match anything.
    if (column.isFilterable === false) return;
    // Only include columns that have fields defined
    if (column.fields && column.fields.length > 0) {
      // First check if this column should use a special change field for filtering
      if (changeFieldMap[column.key]) {
        // Use the special change field for filtering
        const changeField = changeFieldMap[column.key];
        
        // Check if the change field is in the column's fields array
        if (column.fields.includes(changeField)) {
          // Map to the change field but keep the original column name/label
          mappings[column.key] = changeField;
          
          // Add the column with a more descriptive label
          addColumn({
            ...column,
            label: column.label + ' %', // Add % to indicate it's the percentage change
            tooltip: `Filter by percentage change in ${column.label.toLowerCase()}`
          }, changeField);
          
          console.log(`Applied special change field mapping: ${column.key} → ${changeField}`);
        } else {
          // If change field isn't in column fields, just add the column normally
          mappings[column.key] = column.fields[0];
          addColumn(column, column.fields[0]);
        }
      }
      // For columns with a single field, handling is straightforward
      else if (column.fields.length === 1) {
        // Map the column key to its only field
        mappings[column.key] = column.fields[0];
        
        // If this is a change column, add it directly
        if (isPrimaryChangeColumn(column)) {
          addColumn(column, column.fields[0]);
        } else {
          // Add regular column
          addColumn(column, column.fields[0]);
        }
      } 
      // For multi-field columns, create separate filter options for each filterable field
      else {
        // First, check if this column has a specific filterField property
        if (column.filterField) {
          // If column defines a specific field to use for filtering, use that
          mappings[column.key] = column.filterField;
          addColumn(column, column.filterField);
        } 
        // Otherwise use the sortField if available (often the primary field for data purposes)
        else if (column.sortField && typeof column.sortField === 'string') { 
          mappings[column.key] = column.sortField;
          addColumn(column, column.sortField);
        }
        // If no specific filter field is defined, use the first field but also create options for others
        else {
          // Add the main column using first field
          mappings[column.key] = column.fields[0];
          addColumn(column, column.fields[0]);
          
          // Add separate options for additional fields that are filterable
          column.fields.forEach((field, index) => {
            // Skip the first field as it's already included with the column key
            if (index === 0) return;
            
            // Only add if it's a filterable field
            if (isFilterableField(field)) {
              // Check if it's a change field
              const isChange = isChangeField(field);
              const fieldKey = `${column.key}_${isChange ? 'change' : 'field'}_${index}`;
              mappings[fieldKey] = field;
              
              // Create appropriate label
              const fieldLabel = isChange 
                ? getChangeLabel(field, column.label)
                : `${column.label} (${field.replace(/([A-Z])/g, ' $1').trim()})`;
              
              // Add as a separate column option
              addColumn({
                key: fieldKey,
                label: fieldLabel,
                tooltip: `Filter by ${fieldLabel.toLowerCase()}`,
                fields: [field],
                sortField: field
              }, field);
            }
          });
        }
      }
      
      // Check for specifically defined change fields
      if (column.changeField) {
        const changeKey = `${column.key}_change`;
        mappings[changeKey] = column.changeField;
        
        // Add a new column definition for this change field
        addColumn({
          key: changeKey,
          label: getChangeLabel(column.changeField, column.label),
          tooltip: `Change in ${column.label.toLowerCase()}`,
          fields: [column.changeField],
          sortField: column.changeField
        }, column.changeField);
      }
    }
  });
  
  // Group columns by category first, then sort alphabetically within groups
  filterableColumns.sort((a, b) => {
    if (a.category !== b.category) {
      // Sort by category order in the categories array
      const catAIndex = categories.findIndex(c => c.id === a.category);
      const catBIndex = categories.findIndex(c => c.id === b.category);
      return catAIndex - catBIndex;
    }
    // Within same category, sort alphabetically
    return a.label.localeCompare(b.label);
  });
  
  // Add any special case mappings that aren't covered by the column definitions
  const specialMappings = {
    'shortFloat': 'Short %',
    // Add other special cases as needed
  };
  
  return { 
    mappings: { ...mappings, ...specialMappings },
    filterableColumns,
    categories,
    changeFieldMap
  };
};

const CustomFilterModal = ({ isOpen, onClose, onSuccess, loggedInUser }) => {
  // Generate field mappings and filterable columns once when component mounts
  const { mappings: FIELD_MAPPING, filterableColumns, categories, changeFieldMap } = useMemo(() => {
    const result = generateFieldMappings();
    
    // Log special display+change mappings
    console.log('\nDisplay+Change Column Mappings (filters on the change %):');
    Object.entries(result.changeFieldMap || {}).forEach(([uiField, dbField]) => {
      const column = result.filterableColumns.find(col => col.key === uiField);
      const label = column ? column.label : uiField;
      console.log(`  ${label} → ${dbField}`);
    });
    
    // Log the exact mapping from UI fields to database fields
    console.log('\nField mappings (UI field → DB field):');
    Object.entries(result.mappings).forEach(([uiField, dbField]) => {
      console.log(`  ${uiField} → ${dbField}`);
    });
    
    // Log the columns in a more readable format
    console.log('\nFilterable columns by category:');
    
    // Group by category and log
    result.categories.forEach(category => {
      const categoryColumns = result.filterableColumns.filter(col => col.category === category.id);
      if (categoryColumns.length > 0) {
        console.log(`\n== ${category.name} (${categoryColumns.length}) ==`);
        categoryColumns.forEach(column => {
          const dbField = result.mappings[column.key] || 'N/A';
          console.log(`- ${column.label} (UI: ${column.key}, DB: ${dbField})`);
        });
      }
    });
    
    // Log "other" category
    const otherColumns = result.filterableColumns.filter(col => !col.category || col.category === 'other');
    if (otherColumns.length > 0) {
      console.log(`\n== Other (${otherColumns.length}) ==`);
      otherColumns.forEach(column => {
        const dbField = result.mappings[column.key] || 'N/A';
        console.log(`- ${column.label} (UI: ${column.key}, DB: ${dbField})`);
      });
    }
    
    // Count change-related columns
    const changeColumns = result.filterableColumns.filter(c => 
      c.key.includes('change') || 
      c.label.includes('Change') || 
      c.label.includes('%') || 
      c.label.includes('Growth')
    );
    console.log(`\nTotal filterable columns: ${result.filterableColumns.length}, Change columns: ${changeColumns.length}`);
    
    return result;
  }, []);
  
  const [filter, setFilter] = useState({
    name: '',
    description: '',
    conditions: [{ field: 'marketCap', valueCategory: 'high' }],
    requireAll: true // true = AND logic, false = OR logic
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [matchingSymbols, setMatchingSymbols] = useState([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);

  // Use optional chaining to avoid 'cannot read email of undefined'
  const userEmail = loggedInUser?.email || null;
  
  // Helper function to map conditions using field mappings
  const mapConditions = (conditions) => {
    return conditions.map(condition => {
      // If the field has a mapping, use it; otherwise use the original field
      const mappedField = FIELD_MAPPING[condition.field] || condition.field;
      
      // Find the column definition for this field
      const column = filterableColumns.find(col => col.key === condition.field);
      const isDateField = column && column.format === 'date';
      
      // Log the mapping for debugging
      if (mappedField !== condition.field) {
        const label = column ? column.label : condition.field;
        
        // Check if this is a special change field mapping
        if (changeFieldMap[condition.field] === mappedField) {
          console.log(`Special change field mapping: '${label}' (UI field: ${condition.field}) filters on percent change: '${mappedField}'`);
        } else {
          console.log(`Field mapping: '${label}' (UI field: ${condition.field}) → DB field: '${mappedField}'`);
        }
      } else {
        console.log(`Warning: No mapping found for field '${condition.field}', using directly as DB field`);
      }
      
      // Handle date fields differently
      if (isDateField) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const oneWeekLater = new Date(today);
        oneWeekLater.setDate(oneWeekLater.getDate() + 7);
        
        const twoWeeksLater = new Date(today);
        twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
        
        const fourWeeksLater = new Date(today);
        fourWeeksLater.setDate(fourWeeksLater.getDate() + 28);
        
        // Format dates as ISO strings for the database
        let startDate = null;
        let endDate = null;
        
        switch(condition.valueCategory) {
          case 'today':
            startDate = today.toISOString().split('T')[0]; // Just the date part
            endDate = today.toISOString().split('T')[0];
            break;
          case 'tomorrow':
            startDate = tomorrow.toISOString().split('T')[0];
            endDate = tomorrow.toISOString().split('T')[0];
            break;
          case 'this_week':
            startDate = today.toISOString().split('T')[0];
            endDate = oneWeekLater.toISOString().split('T')[0];
            break;
          case 'next_week':
            startDate = oneWeekLater.toISOString().split('T')[0];
            endDate = twoWeeksLater.toISOString().split('T')[0];
            break;
          case 'upcoming':
            startDate = today.toISOString().split('T')[0];
            endDate = fourWeeksLater.toISOString().split('T')[0];
            break;
          case 'past':
            endDate = today.toISOString().split('T')[0];
            // No start date for past (will match all dates before today)
            break;
          default:
            // Default to upcoming
            startDate = today.toISOString().split('T')[0];
            endDate = fourWeeksLater.toISOString().split('T')[0];
        }
        
        return {
          field: mappedField,
          type: 'date_range',
          startDate,
          endDate
        };
      }
      
      // Map value category to database-friendly comparison for non-date fields
      let dbCondition;
      
      // Convert valueCategory to database-friendly condition
      switch(condition.valueCategory) {
        case 'very_high':
          dbCondition = { 
            field: mappedField, 
            percentile: 90,  // Top 10%
            direction: 'top' 
          };
          break;
        case 'high':
          dbCondition = { 
            field: mappedField, 
            percentile: 75,  // Top 25%
            direction: 'top' 
          };
          break;
        case 'medium':
          dbCondition = { 
            field: mappedField, 
            percentile: 50,  // Middle 50%
            direction: 'middle' 
          };
          break;
        case 'low':
          dbCondition = { 
            field: mappedField, 
            percentile: 25,  // Bottom 25%
            direction: 'bottom' 
          };
          break;
        case 'very_low':
          dbCondition = { 
            field: mappedField, 
            percentile: 10,  // Bottom 10%
            direction: 'bottom' 
          };
          break;
        default:
          // Fallback to a default setting if valueCategory is not recognized
          dbCondition = { 
            field: mappedField, 
            percentile: 75,  // Default to top 25%
            direction: 'top' 
          };
      }
      
      return dbCondition;
    });
  };

  const handleAddCondition = () => {
    setFilter(prevFilter => ({
      ...prevFilter,
      conditions: [...prevFilter.conditions, { field: 'marketCap', valueCategory: 'high' }]
    }));
  };

  const handleRemoveCondition = (conditionIndex) => {
    setFilter(prevFilter => ({
      ...prevFilter,
      conditions: prevFilter.conditions.filter((_, index) => index !== conditionIndex)
    }));
  };

  const handleConditionChange = (conditionIndex, field, value) => {
    setFilter(prevFilter => {
      const newConditions = [...prevFilter.conditions];
      
      newConditions[conditionIndex] = { 
        ...newConditions[conditionIndex], 
        [field]: value 
      };
      
      return { ...prevFilter, conditions: newConditions };
    });
  };

  const handleLogicChange = (requireAll) => {
    setFilter(prevFilter => ({
      ...prevFilter,
      requireAll
    }));
  };

  const evaluateFilter = async () => {
    if (filter.conditions.length === 0) {
      setError('Please add at least one condition');
      return;
    }

    setIsEvaluating(true);
    setError('');

    try {
      // Map the field names in the conditions
      const mappedConditions = mapConditions(filter.conditions);

      // Log the URL to debug
      console.log('Calling custom filter endpoint:', CONFIG.API_URLS.CUSTOM_FILTER);
      
      // Call the custom-filter endpoint to evaluate the filter
      const response = await publicFetch(CONFIG.API_URLS.CUSTOM_FILTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filter_criteria: {
            conditions: mappedConditions,
            requireAll: filter.requireAll
          },
          user_id: userEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to evaluate filter criteria');
      }

      const result = await response.json();
      const symbols = result.symbols || [];
      
      setMatchingSymbols(symbols);
      setPreviewCount(symbols.length);
      
      if (symbols.length === 0) {
        setError('No stocks match your filter criteria. Try adjusting your conditions.');
      } else {
        setError(`Found ${symbols.length} matching stocks. Examples: ${symbols.slice(0, 5).join(', ')}${symbols.length > 5 ? '...' : ''}`);
      }
    } catch (error) {
      console.error('Error evaluating filter:', error);
      setError(error.message || 'Failed to evaluate filter');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!userEmail) {
      setError('You must be logged in to create custom filters.');
      return;
    }

    if (!filter.name.trim()) {
      setError('Please enter a name for your filter');
      return;
    }

    if (filter.conditions.length === 0) {
      setError('Please add at least one condition');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Map the field names in the conditions
      const mappedConditions = mapConditions(filter.conditions);

      // Use previously evaluated symbols if available, otherwise evaluate the filter
      let symbolsToUse = [];
      
      if (matchingSymbols && matchingSymbols.length > 0) {
        symbolsToUse = matchingSymbols;
      } else {
        setError('Evaluating filter criteria...');
        
        // Log the URL to debug
        console.log('Calling custom filter endpoint in submit:', CONFIG.API_URLS.CUSTOM_FILTER);
        
        // Get the matching symbols by calling the custom-filter endpoint directly
        const filterEvalResponse = await publicFetch(CONFIG.API_URLS.CUSTOM_FILTER, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filter_criteria: {
              conditions: mappedConditions,
              requireAll: filter.requireAll
            },
            user_id: userEmail
          })
        });
        
        if (!filterEvalResponse.ok) {
          const errorData = await filterEvalResponse.json();
          throw new Error(errorData.error || 'Failed to evaluate filter criteria');
        }
        
        const filterResult = await filterEvalResponse.json();
        symbolsToUse = filterResult.symbols || [];
        
        setError('');
      }
      
      if (symbolsToUse.length === 0) {
        setError('No stocks match your filter criteria. Please adjust your conditions.');
        setIsLoading(false);
        return;
      }
      
      // Create a filter configuration object
      const filterConfig = {
        // Store the original conditions (with column keys, not mapped fields)
        // This ensures consistent mapping when the filter is loaded later
        conditions: filter.conditions,
        requireAll: filter.requireAll,
        last_evaluated: new Date().toISOString()
      };
      
      // Make sure we're stringifying this properly
      const filterConfigStr = JSON.stringify(filterConfig);
      
      // Prepare filter data for saving with the matching symbols
      const listData = {
        name: filter.name,
        description: filter.description || `Custom filter with ${filter.conditions.length} conditions`,
        tooltip: `Custom filter: ${filter.name}`,
        type: 'custom_filter',
        filter_config: filterConfigStr,
        icon: 'FilterAlt',
        category_id: 'personal',
        is_public: false,
        owner_id: userEmail,
        symbols: symbolsToUse // Send as a plain array, don't stringify here
      };

      // Add detailed logging
      console.log('List data before creating:', JSON.stringify(listData, null, 2));
      console.log('Symbols type:', Array.isArray(symbolsToUse) ? 'Array' : typeof symbolsToUse);
      console.log('First few symbols:', Array.isArray(symbolsToUse) ? symbolsToUse.slice(0, 5) : 'Not an array');
      console.log('Filter config type:', typeof listData.filter_config);
      console.log('Filter config:', listData.filter_config.substring(0, 100) + '...');  // Log the first part
      console.log('Checking for undefined values:');
      for (const [key, value] of Object.entries(listData)) {
        console.log(`- ${key}: ${value === undefined ? 'UNDEFINED' : 'defined'}`);
      }

      // Call the API to create the filter - pass only listData
      const response = await createUserList(listData);
      
      // Handle success
      if (response && response.id) {
        // Show success message with symbol count
        setError('');
        onSuccess?.({
          ...response,
          matchCount: symbolsToUse.length
        });
        onClose();
      } else {
        throw new Error('Failed to create custom filter');
      }
    } catch (error) {
      console.error('Error creating custom filter:', error);
      // Try to extract more detailed error information
      let errorMessage = error.message || 'Failed to create custom filter';
      if (error.response) {
        try {
          const responseData = await error.response.json();
          console.error('Error response data:', responseData);
          errorMessage = responseData.error || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-[60vw] transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-2xl font-semibold leading-6 text-gray-900 mb-4 pb-2 border-b border-gray-200"
                >
                  Create Custom Filter
                </Dialog.Title>
                
                <div className="mt-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter Name
                    </label>
                    <input
                      type="text"
                      value={filter.name}
                      onChange={(e) => setFilter({...filter, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., High Growth Tech Stocks"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      value={filter.description}
                      onChange={(e) => setFilter({...filter, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe your filter"
                      rows={2}
                    />
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Filter Conditions
                      </label>
                      <div className="flex space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            checked={filter.requireAll}
                            onChange={() => handleLogicChange(true)}
                            className="form-radio text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Match ALL conditions (AND)</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            checked={!filter.requireAll}
                            onChange={() => handleLogicChange(false)}
                            className="form-radio text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Match ANY condition (OR)</span>
                        </label>
                      </div>
                    </div>
                    
                    {filter.conditions.map((condition, conditionIndex) => {
                      const currentColumn = filterableColumns.find(col => col.key === condition.field);
                      const isDateField = currentColumn && currentColumn.format === 'date';
                      const isExcludedField = currentColumn && 
                                        (currentColumn.key === 'name' || 
                                         currentColumn.key === 'notes' || 
                                         currentColumn.isCompanyColumn ||
                                         currentColumn.isActionsColumn);

                      return (
                      <div key={conditionIndex} className="condition-row mb-3 flex flex-wrap items-center gap-2">
                        <select
                          value={condition.field}
                          onChange={(e) => handleConditionChange(conditionIndex, 'field', e.target.value)}
                          className="condition-select enhanced-select"
                          style={{
                            maxHeight: '300px',
                            fontSize: '14px'
                          }}
                        >
                          {/* Group columns by category */}
                          {categories.map(category => {
                            const categoryColumns = filterableColumns.filter(col => 
                              col.category === category.id && 
                              // Filter out excluded fields 
                              !(col.key === 'name' || col.key === 'notes' || col.isCompanyColumn || col.isActionsColumn)
                            );
                            
                            // Only render category if it has columns
                            if (categoryColumns.length === 0) return null;
                            
                            return (
                              <optgroup key={category.id} label={category.name}>
                                {categoryColumns.map(column => (
                                  <option 
                                    key={column.key} 
                                    value={column.key}
                                    title={column.tooltip || `Filter by ${column.label}`}
                                  >
                                    {column.label}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })}
                          
                          {/* Add "Other" category for any remaining columns */}
                          {filterableColumns.filter(col => 
                            (!col.category || col.category === 'other') && 
                            // Filter out excluded fields
                            !(col.key === 'name' || col.key === 'notes' || col.isCompanyColumn || col.isActionsColumn)
                          ).length > 0 && (
                            <optgroup label="Other">
                              {filterableColumns
                                .filter(col => 
                                  (!col.category || col.category === 'other') && 
                                  // Filter out excluded fields
                                  !(col.key === 'name' || col.key === 'notes' || col.isCompanyColumn || col.isActionsColumn)
                                )
                                .map(column => (
                                  <option 
                                    key={column.key} 
                                    value={column.key}
                                    title={column.tooltip || `Filter by ${column.label}`}
                                  >
                                    {column.label}
                                  </option>
                                ))}
                            </optgroup>
                          )}
                        </select>
                        
                        {isDateField ? (
                          // Date field uses simplified relative date ranges
                          <select
                            value={condition.valueCategory || 'high'}
                            onChange={(e) => handleConditionChange(conditionIndex, 'valueCategory', e.target.value)}
                            className="condition-select"
                          >
                            <option value="high">Within Next Week</option>
                            <option value="low">Beyond Next Week</option>
                          </select>
                        ) : (
                          // Standard high/low selector for numeric fields
                          <select
                            value={condition.valueCategory}
                            onChange={(e) => handleConditionChange(conditionIndex, 'valueCategory', e.target.value)}
                            className="condition-select"
                          >
                            <option value="high">High</option>
                            <option value="low">Low</option>
                          </select>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => handleRemoveCondition(conditionIndex)}
                          className="remove-condition-btn"
                        >
                          Remove
                        </button>
                      </div>
                      );
                    })}
                    
                    <button
                      type="button"
                      onClick={handleAddCondition}
                      className="add-condition-btn mt-2"
                    >
                      Add Condition
                    </button>
                  </div>
                </div>
                
                {error && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
                    {error}
                  </div>
                )}
                
                <div className="flex justify-between mt-4">
                  <button
                    type="button"
                    onClick={evaluateFilter}
                    disabled={isEvaluating || filter.conditions.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEvaluating ? 'Evaluating...' : 'Preview Results'}
                  </button>
                  
                  {previewCount > 0 && (
                    <div className="text-sm text-gray-600">
                      {previewCount} stocks match your criteria
                    </div>
                  )}
                </div>
                
                <div className="dialog-actions mt-6 flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={onClose} 
                    className="cancel-btn px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="save-btn px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating...' : 'Create Filter'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CustomFilterModal;

// Add a helper function to generate a human-readable description of filter conditions
export const generateFilterDescription = (conditions, requireAll) => {
  if (!conditions || conditions.length === 0) {
    return 'No conditions defined';
  }
  
  // Map field names to more readable versions
  const fieldNameMap = {
    'marketCap': 'Market Cap',
    'shortFloat': 'Short %',
    'Short %': 'Short %',
    'peRatio': 'P/E Ratio',
    'forwardPE': 'Forward P/E',
    'priceToBook': 'Price to Book',
    'divYield': 'Dividend Yield',
    'trailingEPS': 'Trailing EPS',
    'forwardEPS': 'Forward EPS',
    'pegRatio': 'PEG Ratio',
    'revenueGrowth': 'Revenue Growth',
    'grossMargin': 'Gross Margin',
    'operatingMargin': 'Operating Margin',
    'currentPrice': 'Current Price',
    'fiftyDayAvg': '50-Day Avg',
    'twoHundredDayAvg': '200-Day Avg',
    'fiftyTwoWeekHigh': '52-Week High',
    'fiftyTwoWeekLow': '52-Week Low',
    'outlook': 'Business Outlook',
    'date': 'Event Date'
  };
  
  // Map value categories to readable descriptions
  const valueCategoryMap = {
    'high': 'high',
    'low': 'low',
    // For backward compatibility
    'very_high': 'high',
    'top': 'high',
    'medium': 'medium',
    'middle': 'medium',
    'very_low': 'low',
    'bottom': 'low'
  };
  
  // Map date value categories to readable descriptions
  const dateCategoryMap = {
    'high': 'within next week',
    'low': 'beyond next week or in the past',
    // For backward compatibility
    'today': 'today',
    'tomorrow': 'tomorrow',
    'this_week': 'within this week',
    'next_week': 'next week',
    'upcoming': 'within the next 4 weeks',
    'past': 'in the past'
  };
  
  // Format each condition as a readable string
  const conditionStrings = conditions.map(condition => {
    const { field, valueCategory } = condition;
    const readableField = fieldNameMap[field] || field;
    
    // Check if this is a date field
    const isDateField = field === 'date';
    
    if (isDateField) {
      const readableDateCategory = dateCategoryMap[valueCategory] || valueCategory;
      return `${readableField} is ${readableDateCategory}`;
    } else {
      const readableCategory = valueCategoryMap[valueCategory] || valueCategory;
      return `${readableField} is ${readableCategory}`;
    }
  });
  
  // Join conditions with AND or OR
  const logic = requireAll ? 'AND' : 'OR';

  // For tooltip display, use line breaks between conditions for better readability
  if (conditions.length > 1) {
    return `Filter Logic: Match ${requireAll ? 'ALL' : 'ANY'} conditions\n\nConditions:\n• ${conditionStrings.join('\n• ')}`;
  } else {
    return conditionStrings.join(` ${logic} `);
  }
}; 